// Trace export (IMPLEMENTATION §3 M12 "export trace", §0.1 / §6.1 parity): a start state + the command log
// + the defs, replayable in Node (tools/replay-trace.ts, tests) to the same full-state hash().
// The live sim caps its command log (ui.json debug.commandLogMax, M5 #19), so the recorder keeps a BASE: a
// copy of the state and the log seq it starts from. It starts at the sim's start state (boot, save load,
// loadSim, data reload: `onSim`) and moves forward in half-window steps (a second copy taken halfway, promoted
// when the window is full), so an export always covers between window/2 and window commands and never needs
// a log entry the sim already dropped. Commands are copied by the sim when logged (JSON), rejected ones
// included: a replay re-rejects them.
import type { SimObserver } from '../game/observer.ts';
import type { LogEntry } from '../sim/commands.ts';
import { hashValue } from '../sim/core/hash.ts';
import type { SimDefs } from '../sim/defs.ts';
import { Simulation } from '../sim/Simulation.ts';
import { cloneState } from '../sim/setup.ts';
import type { SimState } from '../sim/state.ts';

export const TRACE_FORMAT = 'gunmerger.trace';

export interface TraceCheckpoint {
  tick: number;
  hash: string;
  cash: number;
}

export interface TraceMeta {
  version: string;
  build: string;
  /** Wall ms of the export. */
  wallMs: number;
  /** Telemetry session number. */
  session: number;
}

/** Same core fields as a golden trace (tools/lib/golden.ts): {seed, defsHash, initialHash, initial, commands, checkpoints}. */
export interface DebugTrace {
  format: typeof TRACE_FORMAT;
  v: 1;
  /** The cold-open seed of this game (null: it started from a save, a scenario or loadSim). */
  seed: number | null;
  /** hashValue(defs): the replay checks it. */
  defsHash: string;
  /** The compiled defs the sim ran on (a DEV hot reload or loadSim may differ from simdefs.json). */
  defs: SimDefs;
  /** hashValue(initial). */
  initialHash: string;
  /** Start state of the window (the game's start, or a later base once the command log was capped). */
  initial: SimState;
  /** Log entries {t, seq, cmd} since the base, in order. */
  commands: LogEntry[];
  /** [the state at the export]. */
  checkpoints: TraceCheckpoint[];
  meta: TraceMeta;
}

export interface TraceRecorder extends SimObserver {
  /** The trace of `sim` (the running game) now. Throws if the window lost a command (never expected). */
  trace(sim: Simulation, seed: number | null, meta: TraceMeta): DebugTrace;
}

interface Base {
  state: SimState;
  seq: number;
}

/** The seq the next logged command gets. */
function nextSeq(sim: Simulation): number {
  const last = sim.log[sim.log.length - 1];
  return last ? last.seq + 1 : 0;
}

/** `window` = the live sim's log cap (ui.json debug.commandLogMax); < 1 = unbounded. */
export function createTraceRecorder(window: number): TraceRecorder {
  const half = Math.max(1, Math.floor(window / 2));
  let a: Base | null = null;
  let b: Base | null = null;
  const snap = (sim: Simulation): Base => ({ state: cloneState(sim.state), seq: nextSeq(sim) });
  return {
    onSim(sim) {
      a = snap(sim);
      b = null;
    },
    onCommand(_cmd, _res, _cash, _evs, sim) {
      if (a === null || window < 1) return;
      const n = nextSeq(sim);
      if (b === null && n - a.seq >= half) b = snap(sim);
      if (n - a.seq >= window) {
        a = b ?? snap(sim);
        b = snap(sim);
      }
    },
    trace(sim, seed, meta) {
      if (a === null) throw new Error('trace: no start state recorded');
      const from = a.seq;
      const commands = sim.log
        .filter((e) => e.seq >= from)
        .map((e) => JSON.parse(JSON.stringify(e)));
      if (commands.length !== nextSeq(sim) - from)
        throw new Error(`trace: the command log lost entries after seq ${from}`);
      const initial = cloneState(a.state);
      return {
        format: TRACE_FORMAT,
        v: 1,
        seed,
        defsHash: hashValue(sim.defs),
        defs: JSON.parse(JSON.stringify(sim.defs)) as SimDefs,
        initialHash: hashValue(initial),
        initial,
        commands: commands as LogEntry[],
        checkpoints: [{ tick: sim.state.tick, hash: sim.hash(), cash: sim.state.wallet.cash }],
        meta,
      };
    },
  };
}

export interface ReplayResult {
  ok: boolean;
  /** One line per checkpoint (or the reason the trace is unusable). */
  lines: string[];
  /** The replayed hash at the last checkpoint. */
  hash: string;
  tick: number;
}

/** Structural check of untrusted JSON (a file from a phone). */
export function checkTrace(x: unknown): DebugTrace {
  const t = x as Partial<DebugTrace> | null;
  if (typeof t !== 'object' || t === null || t.format !== TRACE_FORMAT || t.v !== 1)
    throw new Error(`not a ${TRACE_FORMAT} v1 file`);
  if (!t.initial || !Array.isArray(t.commands) || !Array.isArray(t.checkpoints))
    throw new Error('trace: initial / commands / checkpoints missing');
  if (t.checkpoints.length === 0) throw new Error('trace: no checkpoint');
  return t as DebugTrace;
}

/**
 * Replays a trace (on `defs`, default the trace's own) from its start state through every command and
 * compares each checkpoint's hash. Deterministic and pure: the Node side of the Chromium ↔ Node parity.
 */
export function replayTrace(trace: DebugTrace, defs: SimDefs = trace.defs): ReplayResult {
  const lines: string[] = [];
  let ok = true;
  const dh = hashValue(defs);
  if (dh !== trace.defsHash) {
    lines.push(`defs ${dh} ≠ trace defsHash ${trace.defsHash}`);
    ok = false;
  }
  const ih = hashValue(trace.initial);
  if (ih !== trace.initialHash) {
    lines.push(`initial ${ih} ≠ initialHash ${trace.initialHash}`);
    ok = false;
  }
  let hash = '';
  let tick = trace.initial.tick;
  let sim: Simulation | null = null;
  for (const cp of trace.checkpoints) {
    // a fresh replay per checkpoint keeps it simple (traces have one or a few checkpoints)
    sim = Simulation.replay(defs, trace.initial, trace.commands, cp.tick);
    hash = sim.hash();
    tick = sim.state.tick;
    const same = hash === cp.hash && sim.state.wallet.cash === cp.cash;
    if (!same) ok = false;
    lines.push(
      `tick ${cp.tick}: ${hash}${same ? ' =' : ` ≠ ${cp.hash}`} (cash ${sim.state.wallet.cash})`,
    );
  }
  return { ok, lines, hash, tick };
}
